import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Flag, Send } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { AceHeart } from "../components/AceHeart";
import { TarotCard } from "../components/TarotCard";
import { AccessExpired } from "../components/AccessExpired";
import { ReportModal } from "../components/ReportModal";
import { BlockButton } from "../components/BlockButton";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

// Les 5 cartes APINITY pour briser la glace — fixes, communes à toutes les conversations
const CONVO_CARDS = [
  { theme: "Enfants", icon: "baby", question: "Et toi, tu imagines des enfants dans ton futur ?" },
  { theme: "Proximité", icon: "pin", question: "L'amour à distance, tu y crois ou c'est hors de question ?" },
  { theme: "Avenir", icon: "compass", question: "Tu te vois où, dans 5 ans ?" },
  { theme: "Passions", icon: "art", question: "Quelle passion pourrais-tu partager des heures avec moi ?" },
  { theme: "Musique", icon: "star", question: "Quelle chanson te ressemble en ce moment ?" },
];

export default function Chat() {
  const { matchId } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [expired, setExpired] = useState(false);
  const [convoFlipped, setConvoFlipped] = useState(false);
  const [reporting, setReporting] = useState(false);
  const navigate = useNavigate();
  const bottomRef = useRef(null);

  const load = () =>
    api
      .get(`/matches/${matchId}/messages`)
      .then((r) => {
        setData(r.data);
        api.post(`/matches/${matchId}/read`).catch(() => {});
      })
      .catch((e) => {
        if (e?.response?.status === 402) setExpired(true);
        else toast.error(formatApiError(e));
      });

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [data?.messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      await api.post(`/matches/${matchId}/messages`, { text: text.trim() });
      setText("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const sendQuestion = async (q) => {
    if (sending) return;
    setSending(true);
    try {
      await api.post(`/matches/${matchId}/messages`, { text: q });
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const other = data?.other;

  if (expired) return <AccessExpired />;

  return (
    <div className="relative flex h-[calc(100vh-65px)] flex-col overflow-hidden bg-gradient-to-b from-[#F9E4E0] via-[#FAF3EC] to-[#F3D5CF]" data-testid="chat-page">
      <img src="/assets/mandala-sun.jpeg" alt="" className="pointer-events-none fixed -left-40 -top-40 h-[560px] w-[560px] rounded-full object-cover opacity-[0.14]" />
      <img src="/assets/mandala-card.jpeg" alt="" className="pointer-events-none fixed -bottom-48 -right-48 h-[620px] w-[620px] rotate-12 rounded-full object-cover opacity-[0.12]" />
      <div className="pointer-events-none fixed inset-0 bg-gradient-to-tr from-[#5C1E28]/5 via-transparent to-[#D97B8F]/10" />

      <div className="relative mx-auto flex w-full max-w-3xl flex-1 flex-col px-4 sm:px-8">
      <div className="relative flex items-center gap-4 border-b border-[#D97B8F]/25 py-4">
        <Link to="/matches" data-testid="chat-back-link" className="rounded-full p-2 text-clay transition-colors hover:bg-[#D97B8F]/15 hover:text-[#C4637A]">
          <ArrowLeft size={18} />
        </Link>
        {other && (
          <>
            {other.avatar ? (
              <img src={other.avatar} alt={other.name} className="h-11 w-11 rounded-full border-2 border-[#D97B8F]/60 object-cover" />
            ) : (
              <div className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-[#D97B8F]/60 bg-[#FBEAE8] font-serif text-lg text-[#C4637A]">{other.name?.[0]}</div>
            )}
            <div className="flex-1">
              <h1 className="font-serif text-lg font-semibold text-ink" data-testid="chat-other-name">{other.name}</h1>
              <p className="font-cinzel text-[9px] tracking-[0.25em] text-gold">{other.archetype_label}</p>
            </div>
            {data.compatibility != null && (
              <span className="rounded-full border border-[#D97B8F]/40 bg-[#D97B8F]/12 px-3 py-1 text-xs font-semibold text-[#C4637A]" data-testid="chat-compatibility">
                Compatibilité · {data.compatibility}%
              </span>
            )}
            {/* Signalement & blocage de la conversation */}
            <button
              onClick={() => setReporting(true)}
              data-testid="chat-report-button"
              title={`Signaler ${other.name}`}
              className="flex h-9 w-9 items-center justify-center rounded-full border border-[#D97B8F]/30 bg-white text-clay transition-colors hover:border-terra hover:text-terra"
            >
              <Flag size={15} />
            </button>
            <BlockButton compact targetId={other.id} targetName={other.name} onBlocked={() => navigate("/matches")} />
          </>
        )}
      </div>

      <div className="relative flex-1 space-y-3 overflow-y-auto py-6" data-testid="chat-messages">
        {data?.messages?.length === 0 && (
          <p className="py-10 text-center font-serif text-lg italic text-stone2">« Certaines personnes éveillent quelque chose que l'on ne cherchait même plus. » <AceHeart size={16} className="mb-1 inline-block" /></p>
        )}
        {data?.messages?.map((m) => {
          const mine = m.sender_id === user?.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[68%] rounded-2xl px-3.5 py-2 text-[13px] leading-relaxed shadow-sm sm:max-w-[60%] sm:px-4 sm:py-2.5 sm:text-sm ${
                  mine ? "rounded-br-sm bg-gradient-to-br from-[#D97B8F] to-[#B53A55] text-white" : "rounded-bl-sm border border-[#D97B8F]/25 bg-white text-ink"
                }`}
              >
                {m.text}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <div className="relative pb-5">
        {/* Les 5 cartes APINITY pour briser la glace */}
        <div className="mb-3" data-testid="convo-cards">
          <p className="mb-2 text-center font-cinzel text-[9px] tracking-[0.3em] text-gold">LES CARTES POUR BRISER LA GLACE</p>
          <div className="flex justify-start gap-2 overflow-x-auto pb-1 sm:justify-center sm:gap-2.5">
            {CONVO_CARDS.map((c, i) => (
              <div key={c.theme} className="w-[68px] shrink-0 sm:w-24">
                <TarotCard
                  testid={`convo-card-${i}`}
                  variant={i}
                  flipped={convoFlipped}
                  onClick={() => (convoFlipped ? sendQuestion(c.question) : setConvoFlipped(true))}
                  className="aspect-[5/8]"
                >
                  <div className="flex h-full flex-col items-center justify-center gap-1 p-1.5 text-center">
                    <span className="font-cinzel text-[7px] tracking-[0.18em] text-terra sm:text-[8px]">{c.theme.toUpperCase()}</span>
                    <p className="text-[8px] leading-tight text-clay sm:text-[10px]">{c.question}</p>
                  </div>
                </TarotCard>
              </div>
            ))}
          </div>
          <p className="mt-1.5 text-center text-[10px] text-stone2" data-testid="convo-cards-hint">
            {convoFlipped ? "Touchez une carte pour envoyer sa question dans la conversation" : "Touchez une carte pour toutes les révéler"}
          </p>
        </div>
        <form onSubmit={send} className="flex items-center gap-3">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-testid="chat-input"
            placeholder="Écrivez votre message…"
            className="flex-1 rounded-full border border-[#D97B8F]/30 bg-white px-5 py-3 text-sm text-ink outline-none transition-colors focus:border-[#D97B8F]"
          />
          <button
            type="submit"
            disabled={!text.trim() || sending}
            data-testid="chat-send-button"
            className="flex h-11 w-11 items-center justify-center rounded-full bg-[#D97B8F] text-white shadow-boho transition-all duration-300 hover:scale-105 hover:bg-[#C4637A] disabled:opacity-40"
          >
            <Send size={17} />
          </button>
        </form>
      </div>
      </div>

      {/* Modale de signalement */}
      {reporting && other && (
        <ReportModal target={other} context="conversation" onClose={() => setReporting(false)} />
      )}
    </div>
  );
}
